import { App } from 'hydra-data-types';
import { isNonEmptyString } from 'ts-utils';
import { hasMedicareAccessEntryInURL } from '../config/AssessmentUtils';
import { CoverageScreenName, MedicareAccessScreenName } from '../config/FlowTypes';
import {
  HEALTH_COVER_MEDICARE_ADVANTAGE,
  HEALTH_COVER_MEDICARE_ORIGINAL,
} from '../config/InsuranceProviders';
import { NervaControlScreenName } from '../config/nerva/NervaControlScreenNames';
import { StoreStateInternal } from '../store/Store';
import { Environment } from './Environment';

export const MEDICARE_ACCESS_ROUTE = 'isMedicareAccess' as const;
export const FREE_65_ACCESS_ROUTE = 'isEligibleForFree65Access' as const;
export const US_65_PLUS_ROUTE = 'isUs65Plus' as const;
export const DIRECT_MEDICARE_ROUTE = 'isDirectMedicareFlow' as const;

/** The 20%-off coupon applied to the direct (D2C) Medicare flow at the payment page. */
export const DIRECT_MEDICARE_COUPON_CODE = 'DKMRPT';

/** USPS codes of the states where Mindset can enrol Original Medicare users in CMS ACCESS. */
export const MEDICARE_ACCESS_STATES: readonly string[] = ['DE', 'NM', 'OH', 'VA'];

function getUserDataAge(state: StoreStateInternal): number | undefined {
  const age = state.userData.age?.[0];
  return age === undefined ? undefined : Number(age);
}

/**
 * Whether the user is a US resident aged 65+. Derived from age + country only — it does NOT
 * require a referral, so it applies to both referred (HCP) and direct (D2C) flows. Referral age
 * takes precedence when present, but country is always the user's geolocated country.
 *
 * While no age is known, a 65+ re-engagement link (`entry=medicare-access`) asserts 65+ — CIO
 * only sends those to that cohort. A real age always wins once login restores userData.age, so
 * the whole Medicare flow agrees with the entry gate and self-corrects on forwarded links.
 */
export function isUs65Plus(state: StoreStateInternal): boolean {
  if (Environment.APP !== App.Nerva || state.geolocation.country !== 'US') return false;
  const age = state.referral.age ?? getUserDataAge(state);
  if (age !== undefined) return Number(age) >= 65;
  return hasMedicareAccessEntryInURL();
}

/**
 * Whether the user is a US resident aged under 65. Mirrors {@link isUs65Plus} for the under-65
 * cohort; requires a known age so users without one fall through to the standard payment path
 * rather than the Medicare branch.
 */
export function isUsUnder65(state: StoreStateInternal): boolean {
  const country = state.geolocation.country;
  const age = Number(state.referral.age ?? getUserDataAge(state));
  return Environment.APP === App.Nerva && country === 'US' && Number.isFinite(age) && age < 65;
}

/**
 * US users who aren't 65+, including those whose age isn't known yet — the audience for the
 * under-65 insurance coverage flow (`CoverageConfig.ts`). Unlike {@link isUsUnder65} this doesn't
 * require an age, so a US user with no age on file still collects coverage rather than skipping it.
 */
export function isUsNon65Plus(state: StoreStateInternal): boolean {
  return Environment.APP === App.Nerva && state.geolocation.country === 'US' && !isUs65Plus(state);
}

/**
 * The screens whose answer carries the user's Medicare/insurance type: referred health-cover,
 * D2C 65+ pre-sign-up, and the D2C under-65 insurance-provider dropdown (Original Medicare
 * option). {@link getMedicareType} reads them in this priority order, so when one is answered the
 * others must be cleared (see MERGE_USER_DATA) or a stale account answer shadows the fresh one.
 */
export const MEDICARE_TYPE_SCREEN_KEYS = [
  MedicareAccessScreenName.MedicareAccessHealthCover,
  MedicareAccessScreenName.MedicareAccessDirectHealthCover,
  NervaControlScreenName.InsuranceProvider,
] as const;

/**
 * The Medicare type the user selected. Single source for reading it, so the referred flow and the
 * D2C flow (which ask the same question from different screens) stay in sync — only add a key here.
 */
function getMedicareType(state: StoreStateInternal): string | undefined {
  const userData = state.userData as Partial<
    Record<(typeof MEDICARE_TYPE_SCREEN_KEYS)[number], string[]>
  >;
  return (
    userData[MedicareAccessScreenName.MedicareAccessHealthCover]?.[0] ??
    userData[MedicareAccessScreenName.MedicareAccessDirectHealthCover]?.[0] ??
    userData[NervaControlScreenName.InsuranceProvider]?.[0]
  );
}

/**
 * Under-65 users can hold Original Medicare too (e.g. via disability). They say so either on the
 * coverage plan-type screen or by picking it in the referred flow's insurance-provider dropdown,
 * which then skips the plan-type screen. Neither key joins MEDICARE_TYPE_SCREEN_KEYS: that list's
 * clear-on-answer rule would wipe the provider answer collected one screen earlier, and no stale
 * answer can shadow here because the plan-type screen is re-answered whenever it is shown.
 */
const UNDER_65_ORIGINAL_MEDICARE_KEYS = [
  CoverageScreenName.CoveragePlanType,
  MedicareAccessScreenName.MedicareAccessInsuranceProvider,
] as const;

/**
 * Whether a US under-65 user chose "I'll pay for Nerva myself" on the insurance-provider screen.
 * They skip the coverage screens and, having declined the coverage check, the Medicare ACCESS
 * pathway too — enrolling them in Medicare paperwork would contradict that choice.
 */
export function hasOptedForSelfPay(state: StoreStateInternal): boolean {
  return isUsNon65Plus(state) && state.userData.coverageSelfPay?.[0] === 'Yes';
}

function isUnder65OriginalMedicare(state: StoreStateInternal): boolean {
  if (!isUsNon65Plus(state)) return false;
  const userData = state.userData as Partial<
    Record<(typeof UNDER_65_ORIGINAL_MEDICARE_KEYS)[number], string[]>
  >;
  return UNDER_65_ORIGINAL_MEDICARE_KEYS.some(
    (key) => userData[key]?.[0] === HEALTH_COVER_MEDICARE_ORIGINAL
  );
}

/**
 * Whether the user answered Original Medicare: on a Medicare-type (health cover) screen, or — for
 * US under-65 users — on the coverage plan-type screen / referred insurance-provider dropdown. A US
 * under-65 user who opted to self-pay never counts, whatever they picked. Ignores where they live,
 * so only use it to route to the state question; everything else should use
 * {@link isMedicareOriginal}.
 */
export function hasOriginalMedicareAnswer(state: StoreStateInternal): boolean {
  if (hasOptedForSelfPay(state)) return false;
  return (
    getMedicareType(state) === HEALTH_COVER_MEDICARE_ORIGINAL || isUnder65OriginalMedicare(state)
  );
}

/**
 * The user's US state (USPS code): from the coverage address when the under-65 coverage flow found
 * one, otherwise from whichever state screen they answered.
 */
function getMedicareAccessState(state: StoreStateInternal): string | undefined {
  const { userData } = state;
  // "I can't find my address" leaves any previously picked address components behind.
  const addressState =
    userData.coverageAddressNotFound?.[0] === 'Yes'
      ? undefined
      : userData.coverageAddressState?.[0];
  if (isNonEmptyString(addressState)) return addressState;
  return (
    userData[MedicareAccessScreenName.MedicareAccessStateNoAddress]?.[0] ??
    userData[MedicareAccessScreenName.MedicareAccessState]?.[0]
  );
}

/** Whether the user's state is known, from their coverage address or a state screen. */
export function hasAnsweredMedicareAccessState(state: StoreStateInternal): boolean {
  return getMedicareAccessState(state) !== undefined;
}

/** Whether the user lives in a {@link MEDICARE_ACCESS_STATES} state. False until it's known. */
export function livesInMedicareAccessState(state: StoreStateInternal): boolean {
  const usState = getMedicareAccessState(state);
  return usState !== undefined && MEDICARE_ACCESS_STATES.includes(usState);
}

/**
 * Whether the user has Original Medicare ({@link hasOriginalMedicareAnswer}) and lives in an ACCESS
 * state. Drives the ACCESS pathway in every flow. Users outside those states are treated as not
 * having Original Medicare, so they never enter ACCESS or trigger a CMS check.
 */
export function isMedicareOriginal(state: StoreStateInternal): boolean {
  return hasOriginalMedicareAnswer(state) && livesInMedicareAccessState(state);
}

/** Whether the user selected "Medicare Advantage" on the Medicare-type (health cover) screen. */
export function isMedicareAdvantage(state: StoreStateInternal): boolean {
  return getMedicareType(state) === HEALTH_COVER_MEDICARE_ADVANTAGE;
}

export function isEligibleForFree65Access(state: StoreStateInternal): boolean {
  return (
    isUs65Plus(state) && state.referral.exists === true && state.referral.referralId !== undefined
  );
}

/** Whether the user agreed to CMS ACCESS consent after passing the coverage check. */
export function hasCompletedMedicareAccessConsent(state: StoreStateInternal): boolean {
  const userData = state.userData as Partial<
    Record<MedicareAccessScreenName.MedicareAccessConsent, string[]>
  >;
  const answers = userData[MedicareAccessScreenName.MedicareAccessConsent];
  return answers !== undefined && answers.length > 0;
}

/**
 * One of two paths into {@link isEligibleForUs65RevCatGrant}: D2C (no HCP referral), Medicare
 * Original, and CMS ACCESS consent completed after passing the coverage check. Referred users are
 * excluded — they use {@link isEligibleForFree65Access} instead, which does not require consent
 * or a specific Medicare type.
 */
export function isEligibleForDirectMedicareRevCatGrant(state: StoreStateInternal): boolean {
  return (
    isDirectMedicareFlow(state) &&
    isMedicareOriginal(state) &&
    hasCompletedMedicareAccessConsent(state)
  );
}

/**
 * Whether the user should receive a US 65+ RevenueCat promotional grant. True when either:
 * - referred: {@link isEligibleForFree65Access} (HCP referral, any Medicare type), or
 * - D2C: {@link isEligibleForDirectMedicareRevCatGrant} (Medicare Original + CMS consent only).
 */
export function isEligibleForUs65RevCatGrant(state: StoreStateInternal): boolean {
  return isEligibleForFree65Access(state) || isEligibleForDirectMedicareRevCatGrant(state);
}

/**
 * Whether the user must not enter the CMS ACCESS programme. v1: active RevenueCat `full` (or
 * Firestore paid), or a prior US 65+ grant (`us65Plus.granted` is set only after exclusion checks
 * pass and, for ACCESS-path users, after the CMS flow completes on Us65PlusGrantAccessScreen).
 */
export function isExcludedFromMedicareAccess(state: StoreStateInternal): boolean {
  return state.user.hasPaid === true || state.us65Plus.granted === true;
}

/**
 * Whether the user is in the direct-to-consumer (D2C) Medicare flow: a US 65+ user with no HCP
 * referral. Distinguishes D2C (20% discount applied by WA) from the referred flow (free access /
 * URL coupon) on the shared Medicare Access screens.
 */
export function isDirectMedicareFlow(state: StoreStateInternal): boolean {
  return isUs65Plus(state) && state.referral.exists !== true;
}

export function isEligibleForMedicareAccess(state: StoreStateInternal): boolean {
  return (
    isEligibleForFree65Access(state) &&
    isMedicareOriginal(state) &&
    !isExcludedFromMedicareAccess(state)
  );
}

export function getFree65AccessRoute(
  state: StoreStateInternal
): typeof FREE_65_ACCESS_ROUTE | undefined {
  return isEligibleForFree65Access(state) ? FREE_65_ACCESS_ROUTE : undefined;
}

/** Routes US 65+ users to the Medicare-type screen early in the Control (direct) flow. */
export function getUs65PlusRoute(state: StoreStateInternal): typeof US_65_PLUS_ROUTE | undefined {
  return isUs65Plus(state) ? US_65_PLUS_ROUTE : undefined;
}

/**
 * Routes D2C users to their own destination on shared Medicare Access screens (e.g. the US 65+
 * grant screen sends D2C users to the "already have access" download screen).
 */
export function getDirectMedicareFlowRoute(
  state: StoreStateInternal
): typeof DIRECT_MEDICARE_ROUTE | undefined {
  return isDirectMedicareFlow(state) ? DIRECT_MEDICARE_ROUTE : undefined;
}

export function getMedicareAccessRoute(
  state: StoreStateInternal
): typeof MEDICARE_ACCESS_ROUTE | undefined {
  return isEligibleForMedicareAccess(state) ? MEDICARE_ACCESS_ROUTE : undefined;
}

/**
 * Light client-side check that a US residential address looks complete enough for Medicare
 * enrolment: it must contain a street number and a 5-digit ZIP code (so at least two number
 * groups, one of which is a ZIP). This is a sanity check, not full postal-address validation.
 */
export function isValidUsResidentialAddress(address: string): boolean {
  const trimmed = address.trim();
  const hasZipCode = /\b\d{5}(-\d{4})?\b/.test(trimmed);
  const numberGroups = trimmed.match(/\d+/g) ?? [];
  return hasZipCode && numberGroups.length >= 2;
}

// REGEX FOR MBI NUMBER
// C: Numeric 1 through 9
const C = `[1-9]`;

// N: Numeric 0 through 9
const N = `\\d`;

// A: Alphabetic A-Z excluding S, L, O, I, B, Z
const A = `[AC-HJKMNP-RT-Y]`;

// AN: Either A or N
const AN = `[0-9AC-HJKMNP-RT-Y]`;

/**
 * Full Regex: `C A AN N A AN N A A N N`
 *
 * - `C`: Numeric 1-9
 * - `N`: Numeric 0-9
 * - `A`: Alphabetic A-Z excluding S, L, O, I, B, Z
 * - `AN`: Either A or N
 * @see https://www.cms.gov/medicare/new-medicare-card/understanding-the-mbi-with-format.pdf
 */
export const MBI_REGEX = new RegExp(`^${C}${A}${AN}${N}${A}${AN}${N}${A}${A}${N}${N}$`);
